import { DatePicker } from '@mui/x-date-pickers'
import Label from './Label/Label'
import React from 'react'
import { observer } from 'mobx-react'
import styled from '@emotion/styled'
import theme from '../../global/theme'
import dayjs from 'dayjs'

export interface DateValidationResult {
  isValid: boolean
  errorMessage?: string
}

export type DateValidationFunction = (date: any) => DateValidationResult

interface DatePickerProps {
  autoFocus?: boolean
  closeOnSelect?: boolean
  defaultCalendarMonth?: any
  defaultCalendarYear?: any
  disableFuture?: boolean
  disablePast?: boolean
  disabled?: boolean
  desktopModeMediaQuery?: string
  disableHighlightToday?: boolean
  disableOpenPickerIcon?: boolean
  format?: string
  formatDensity?: 'spacious' | 'dense' | undefined
  label?: string
  loading?: boolean
  value?: any
  maxDate?: any
  minDate?: any
  onAccept?: (date: any) => void
  onChange?: (date: any) => void
  onClose?: () => void
  openTo?: 'year' | 'day' | 'month' | undefined
  onOpen?: () => void
  readonly?: boolean
  error?: boolean
  errorText?: string
  bottomPadding?: boolean
  isRequired?: boolean
  customValidation?: DateValidationFunction
}

const CustomDatePicker = styled(DatePicker)({
  width: '100%',
  '.MuiOutlinedInput-root': {
    borderRadius: '30px',
    fontSize: '16px',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    width: '100%',
  },
  '.MuiOutlinedInput-input': {
    padding: '7.5px 14px',
  },
  '.MuiInputLabel-root': {
    fontSize: '16px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '.MuiFormHelperText-root': {
    marginTop: '-4px',
  },
})

const DateField: React.FC<DatePickerProps> = ({
  autoFocus = false,
  closeOnSelect = true,
  defaultCalendarMonth,
  defaultCalendarYear,
  disableFuture,
  desktopModeMediaQuery,
  disablePast = false,
  format,
  formatDensity = 'dense',
  label,
  value,
  maxDate,
  minDate,
  onAccept,
  onChange,
  onClose,
  openTo = 'day',
  onOpen,
  readonly = true,
  disabled = false,
  error,
  errorText,
  bottomPadding = true,
  isRequired = false,
  customValidation,
}) => {
  const [validationError, setValidationError] = React.useState<string>('')

  const handleChange = (date: any) => {
    if (customValidation && date) {
      const validationResult = customValidation(date)
      if (!validationResult.isValid) {
        setValidationError(validationResult.errorMessage || 'Invalid date')
      } else {
        setValidationError('')
      }
    } else {
      setValidationError('')
    }
    
    if (onChange) {
      onChange(date)
    }
  }

  const handleAccept = (date: any) => {
    if (customValidation && date) {
      const validationResult = customValidation(date)
      if (!validationResult.isValid) {
        setValidationError(validationResult.errorMessage || 'Invalid date')
        return
      } else {
        setValidationError('')
      }
    }
    
    if (onAccept) {
      onAccept(date)
    }
  }

  const hasError = error || !!validationError
  const displayErrorText = validationError || errorText
  return (
    <div style={{ paddingBottom: bottomPadding ? '20px' : 0 }}>
      {label && (
        <Label>
          {label}
          {isRequired && (
            <span style={{ color: theme.colors.secondary }}> *</span>
          )}
        </Label>
      )}
      <CustomDatePicker
        disabled={disabled}
        value={value}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
        onAccept={handleAccept}
        defaultCalendarMonth={defaultCalendarMonth}
        format={format}
        openTo={openTo}
        slotProps={{
          textField: {
            error: hasError,
            helperText: hasError ? displayErrorText : '',
          } as any,
        }}
        onOpen={() => {
          onOpen && onOpen()
          if (window.scrollY < 200) window.scrollTo({ top: 200 })
          // Get the current page scroll position
          window.onscroll = function () {
            window.scrollTo(document.documentElement.scrollLeft, 200)
          }
        }}
        onClose={() => {
          onClose && onClose()
          window.onscroll = function () {}
        }}
        onChange={handleChange}
      />
    </div>
  )
}
export default observer(DateField)
